import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../../components/ui/Button';
import ContactSearchBar from '../../components/ui/ContactSearchBar';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';

// Import components
import ContactCard from './components/ContactCard';
import FilterChips from './components/FilterChips';
import ContactListHeader from './components/ContactListHeader';
import AlphabeticalSections from './components/AlphabeticalSections';
import ContactListEmpty from './components/ContactListEmpty';
import ContactListFilters from './components/ContactListFilters';

const ContactList = () => {
  const navigate = useNavigate();
  
  // State management
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [sortBy, setSortBy] = useState('name');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Mock contact data
  const mockContacts = [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@email.com",
      phone: "+1 (555) 123-4567",
      company: "Tech Solutions Inc",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      birthday: "1990-03-15",
      lastContact: "2025-01-10",
      isFavorite: true,
      tags: ["work", "client"],
      location: "New York, NY",
      notes: "Key client contact for Q1 projects"
    },
    {
      id: 2,
      name: "Michael Chen",
      email: "m.chen@company.com",
      phone: "+1 (555) 987-6543",
      company: "Digital Marketing Pro",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      birthday: "1988-08-22",
      lastContact: "2025-01-08",
      isFavorite: false,
      tags: ["work", "marketing"],
      location: "San Francisco, CA",
      notes: "Marketing specialist, great for campaigns"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      email: "emily.r@gmail.com",
      phone: "+1 (555) 456-7890",
      company: "Freelance Designer",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      birthday: "1992-12-08",
      lastContact: "2025-01-05",
      isFavorite: true,
      tags: ["personal", "creative"],
      location: "Austin, TX",
      notes: "Talented designer, available for projects"
    },
    {
      id: 4,
      name: "David Thompson",
      email: "david.thompson@corp.com",
      phone: "+1 (555) 321-0987",
      company: "Corporate Solutions",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      birthday: "1985-06-30",
      lastContact: "2024-12-20",
      isFavorite: false,
      tags: ["work", "corporate"],
      location: "Chicago, IL",
      notes: "Senior manager, decision maker"
    },
    {
      id: 5,
      name: "Lisa Wang",
      email: "lisa.wang@startup.io",
      phone: "+1 (555) 654-3210",
      company: "InnovateTech Startup",
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
      birthday: "1993-01-18",
      lastContact: "2025-01-12",
      isFavorite: true,
      tags: ["work", "startup", "tech"],
      location: "Seattle, WA",
      notes: "Startup founder, potential partnership"
    },
    {
      id: 6,
      name: "James Wilson",
      email: "j.wilson@consulting.com",
      phone: "+1 (555) 789-0123",
      company: "Business Consulting Group",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      birthday: "1987-04-12",
      lastContact: null,
      isFavorite: false,
      tags: ["work", "consulting"],
      location: "Boston, MA",
      notes: "Business consultant, strategic advisor"
    },
    {
      id: 7,
      name: "Anna Martinez",
      email: "anna.martinez@agency.com",
      phone: "+1 (555) 234-5678",
      company: "Creative Agency",
      avatar: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face",
      birthday: "1991-09-25",
      lastContact: "2025-01-09",
      isFavorite: false,
      tags: ["work", "creative"],
      location: "Los Angeles, CA",
      notes: "Creative director, excellent portfolio"
    },
    {
      id: 8,
      name: "Robert Kim",
      email: "robert.kim@finance.com",
      phone: "+1 (555) 876-5432",
      company: "Financial Services Corp",
      avatar: "https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?w=150&h=150&fit=crop&crop=face",
      birthday: "1984-11-03",
      lastContact: "2024-12-15",
      isFavorite: true,
      tags: ["work", "finance"],
      location: "New York, NY",
      notes: "Financial advisor, trusted partner"
    }
  ];

  // Initialize contacts
  useEffect(() => {
    const loadContacts = async () => {
      setIsLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setContacts(mockContacts);
      setIsLoading(false);
    };

    loadContacts();
  }, []);

  // Filter and sort contacts
  useEffect(() => {
    let filtered = [...contacts];

    // Apply search query
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(contact =>
        contact?.name?.toLowerCase()?.includes(query) ||
        contact?.email?.toLowerCase()?.includes(query) ||
        contact?.phone?.includes(query) ||
        contact?.company?.toLowerCase()?.includes(query) ||
        contact?.tags?.some(tag => tag?.toLowerCase()?.includes(query))
      );
    }

    // Apply filters
    if (activeFilters?.birthdayMonth) {
      filtered = filtered?.filter(contact => {
        if (!contact?.birthday) return false;
        const birthMonth = new Date(contact.birthday)?.getMonth() + 1;
        return birthMonth === parseInt(activeFilters?.birthdayMonth);
      });
    }

    if (activeFilters?.recentActivity) {
      const now = new Date();
      filtered = filtered?.filter(contact => {
        if (!contact?.lastContact && activeFilters?.recentActivity === 'never') return true;
        if (!contact?.lastContact) return false;
        
        const lastContact = new Date(contact.lastContact);
        const diffTime = now - lastContact;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        switch (activeFilters?.recentActivity) {
          case 'today': return diffDays === 0;
          case 'week': return diffDays <= 7;
          case 'month': return diffDays <= 30;
          default: return true;
        }
      });
    }

    if (activeFilters?.favorites) {
      filtered = filtered?.filter(contact => contact?.isFavorite);
    }

    if (activeFilters?.company) {
      filtered = filtered?.filter(contact =>
        contact?.company?.toLowerCase()?.includes(activeFilters?.company?.toLowerCase())
      );
    }

    if (activeFilters?.location) {
      filtered = filtered?.filter(contact =>
        contact?.location?.toLowerCase()?.includes(activeFilters?.location?.toLowerCase())
      );
    }

    if (activeFilters?.tags && activeFilters?.tags?.length > 0) {
      filtered = filtered?.filter(contact =>
        activeFilters?.tags?.some(tag =>
          contact?.tags?.some(contactTag =>
            contactTag?.toLowerCase()?.includes(tag?.toLowerCase())
          )
        )
      );
    }

    if (activeFilters?.hasPhone) {
      filtered = filtered?.filter(contact => contact?.phone);
    }

    if (activeFilters?.hasEmail) {
      filtered = filtered?.filter(contact => contact?.email);
    }

    if (activeFilters?.birthdayThisMonth) {
      const currentMonth = new Date()?.getMonth() + 1;
      filtered = filtered?.filter(contact => {
        if (!contact?.birthday) return false;
        const birthMonth = new Date(contact.birthday)?.getMonth() + 1;
        return birthMonth === currentMonth;
      });
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'name-desc':
          return b?.name?.localeCompare(a?.name);
        case 'birthday':
          if (!a?.birthday && !b?.birthday) return 0;
          if (!a?.birthday) return 1;
          if (!b?.birthday) return -1;
          
          const getDaysUntilBirthday = (birthday) => {
            const today = new Date();
            const birthDate = new Date(birthday);
            const thisYear = today?.getFullYear();
            const nextBirthday = new Date(thisYear, birthDate.getMonth(), birthDate.getDate());
            
            if (nextBirthday < today) {
              nextBirthday?.setFullYear(thisYear + 1);
            }
            
            return Math.ceil((nextBirthday - today) / (1000 * 60 * 60 * 24));
          };
          
          return getDaysUntilBirthday(a?.birthday) - getDaysUntilBirthday(b?.birthday);
        case 'last-contact':
          if (!a?.lastContact && !b?.lastContact) return 0;
          if (!a?.lastContact) return 1;
          if (!b?.lastContact) return -1;
          return new Date(b.lastContact) - new Date(a.lastContact);
        case 'company':
          return (a?.company || '')?.localeCompare(b?.company || '');
        default:
          return 0;
      }
    });

    setFilteredContacts(filtered);
  }, [contacts, searchQuery, activeFilters, sortBy]);

  // Handle contact actions
  const handleEditContact = (contact) => {
    navigate('/add-edit-contact', { state: { contact, mode: 'edit' } });
  };

  const handleMessageContact = (contact) => {
    navigate('/message-templates', { state: { selectedContact: contact } });
  };

  const handleToggleFavorite = (contactId) => {
    setContacts(prev => prev?.map(contact =>
      contact?.id === contactId
        ? { ...contact, isFavorite: !contact?.isFavorite }
        : contact
    ));
  };

  // Selection mode handlers
  const handleToggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedContacts(new Set());
  };

  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(contactId)) {
        newSet?.delete(contactId);
      } else {
        newSet?.add(contactId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedContacts(new Set(filteredContacts.map(c => c.id)));
  };

  const handleDeselectAll = () => {
    setSelectedContacts(new Set());
  };

  // Bulk actions
  const handleBulkDelete = () => {
    if (window.confirm(`Delete ${selectedContacts?.size} contacts? This action cannot be undone.`)) {
      setContacts(prev => prev?.filter(contact => !selectedContacts?.has(contact?.id)));
      setSelectedContacts(new Set());
      setSelectionMode(false);
    }
  };

  const handleBulkExport = () => {
    const selectedContactsData = contacts?.filter(contact => selectedContacts?.has(contact?.id));
    console.log('Exporting contacts:', selectedContactsData);
    // Implement export functionality
  };

  const handleBulkTag = () => {
    // Implement bulk tagging functionality
    console.log('Bulk tagging contacts:', Array.from(selectedContacts));
  };

  // Filter handlers
  const handleRemoveFilter = (filterKey, filterValue) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      
      if (filterKey === 'tags') {
        newFilters.tags = newFilters?.tags?.filter(tag => tag !== filterValue) || [];
        if (newFilters?.tags?.length === 0) {
          delete newFilters?.tags;
        }
      } else {
        delete newFilters?.[filterKey];
      }
      
      return newFilters;
    });
  };

  const handleClearAllFilters = () => {
    setActiveFilters({});
  };

  // Pull to refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    // Simulate refresh
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  }, []);

  const hasActiveFilters = Object.keys(activeFilters)?.length > 0;
  const hasSearchQuery = searchQuery?.trim()?.length > 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <BottomTabNavigation />
        <div className="lg:ml-60 pb-16 lg:pb-0">
          <NavigationBreadcrumbs />
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-text-secondary">Loading contacts...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <BottomTabNavigation />
      <div className="lg:ml-60 pb-16 lg:pb-0">
        <NavigationBreadcrumbs />
        
        {/* Search Bar */}
        <div className="sticky top-0 z-50 bg-background border-b border-border">
          <div className="p-4 lg:p-6">
            <ContactSearchBar
              searchQuery={searchQuery}
              onSearchQuery={setSearchQuery}
              contacts={contacts}
              onContactSelect={(contact) => navigate('/contact-details', { state: { contact } })}
              showFilters={true}
              filters={activeFilters}
              onFilterChange={setActiveFilters}
            />
            
            {/* Filter Chips */}
            <FilterChips
              activeFilters={activeFilters}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
              className="mt-3"
            />
          </div>
        </div>

        {/* List Header */}
        <ContactListHeader
          totalContacts={contacts?.length}
          selectedCount={selectedContacts?.size}
          selectionMode={selectionMode}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onToggleSelectionMode={handleToggleSelectionMode}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          onBulkDelete={handleBulkDelete}
          onBulkExport={handleBulkExport}
          onBulkTag={handleBulkTag}
        />

        {/* Contact List */}
        <div className="flex-1">
          {filteredContacts?.length === 0 ? (
            <ContactListEmpty
              hasSearchQuery={hasSearchQuery}
              searchQuery={searchQuery}
              hasFilters={hasActiveFilters}
              onClearFilters={handleClearAllFilters}
              onAddContact={() => navigate('/add-edit-contact')}
            />
          ) : (
            <AlphabeticalSections
              contacts={filteredContacts}
              renderContact={(contact) => (
                <ContactCard
                  contact={contact}
                  onEdit={handleEditContact}
                  onMessage={handleMessageContact}
                  onToggleFavorite={handleToggleFavorite}
                  isSelected={selectedContacts?.has(contact?.id)}
                  onSelect={handleSelectContact}
                  selectionMode={selectionMode}
                />
              )}
              className="pb-6"
            />
          )}
        </div>

        {/* Floating Action Button */}
        {!selectionMode && (
          <Button
            variant="default"
            size="lg"
            onClick={() => navigate('/add-edit-contact')}
            className="fixed bottom-20 lg:bottom-6 right-6 z-40 rounded-full shadow-modal hover-scale"
            iconName="Plus"
          >
            <span className="hidden sm:inline ml-2">Add Contact</span>
          </Button>
        )}

        {/* Filter Modal */}
        {showFilters && (
          <div className="fixed inset-0 z-100 bg-black/50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
              <ContactListFilters
                filters={activeFilters}
                onFiltersChange={setActiveFilters}
                onClose={() => setShowFilters(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactList;